// DEPRECATED compatibility shim — remove when student/teacher/admin slices stop importing useOrganization
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useAuth } from './AuthContext';
import { getBillingStatus } from '../api/billingApi';
import { logError } from '../utils/errorHandling';
import type { components } from '../api/types.gen';

type BillingStatusResponse = components['schemas']['BillingStatusResponse'];
type MeResponse = components['schemas']['MeResponse'];
// The school embedded on the authenticated user; also the org shape this
// shim exposes as currentOrg / userOrgs.
type School = NonNullable<MeResponse['school']>;

// The value we keep in state: either the optimistic default (only `active`)
// or the real billing-status response with a derived `active` flag. The raw
// fields are optional so the default `{ active: true }` is a valid member.
type BillingStatus = Partial<BillingStatusResponse> & { active: boolean };

interface OrganizationContextValue {
  currentOrg: School | null;
  currentOrgId: string | null;
  userOrgs: School[];
  loading: boolean;
  orgError: string | null;
  billingStatus: BillingStatus;
  switchOrganization: () => void;
  getCurrentOrgRole: () => string | null;
  hasOrgAccess: () => boolean;
  checkBillingStatus: (
    isCancelled?: () => boolean
  ) => Promise<BillingStatus | undefined>;
}

const OrganizationContext = createContext<
  OrganizationContextValue | undefined
>(undefined);

export const useOrganization = (): OrganizationContextValue => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error(
      'useOrganization must be used within an OrganizationProvider'
    );
  }
  return context;
};

// Optimistic default: assume active until the real billing-status fetch
// below resolves, so a brief loading window never flashes App.js's
// RequireBilling lockout screen for a school that isn't actually locked out.
const DEFAULT_BILLING_STATUS: BillingStatus = { active: true };

export const OrganizationProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { currentUser } = useAuth();
  const currentOrgId = currentUser?.school
    ? String(currentUser.school.id)
    : null;

  // Real billing status from passes-api's GET /passes/billing-status
  // (apps/passes-api/src/routes/passes.ts), derived into `active` — App.js's
  // RequireBilling gate treats a school as locked out only when
  // !isPaid && remaining <= 0 — plus the raw isPaid/passCount/
  // freePassLimit/remaining fields that BillingRequired.js and
  // BillingWarningBanner.js consume directly.
  const [billingStatus, setBillingStatus] = useState<BillingStatus>(
    DEFAULT_BILLING_STATUS
  );

  // Guards against a stale response clobbering billingStatus for the wrong
  // user/org: if currentOrgId changes (e.g. logout/user-switch) while a
  // fetch is in flight, the effect below flips `cancelled` on cleanup so an
  // earlier request that resolves after a later one is dropped instead of
  // overwriting state. Mirrors the cancelled-flag pattern in
  // useAdminSetupChecklist.js's fetchCounts.
  const fetchBillingStatus = useCallback(
    async (
      isCancelled: () => boolean = () => false
    ): Promise<BillingStatus | undefined> => {
      if (!currentOrgId) {
        if (!isCancelled()) setBillingStatus(DEFAULT_BILLING_STATUS);
        return DEFAULT_BILLING_STATUS;
      }
      try {
        const result = await getBillingStatus();
        const derived: BillingStatus = {
          ...result,
          active: result.isPaid || (result.remaining ?? 0) > 0,
        };
        if (!isCancelled()) setBillingStatus(derived);
        return derived;
      } catch (err) {
        // Fail open: a transient billing-status fetch failure shouldn't lock
        // a school out of the app — leave the last known billingStatus as-is.
        logError(err, 'client_api', 'OrganizationContext.fetchBillingStatus');
        return undefined;
      }
    },
    [currentOrgId]
  );

  useEffect(() => {
    let cancelled = false;
    fetchBillingStatus(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [fetchBillingStatus]);

  const value: OrganizationContextValue = {
    currentOrg: currentUser?.school
      ? {
          id: currentUser.school.id,
          name: currentUser.school.name,
          timezone: currentUser.school.timezone,
        }
      : null,
    currentOrgId,
    userOrgs: currentUser?.school ? [currentUser.school] : [],
    loading: false,
    orgError: null,
    billingStatus,
    switchOrganization: () => {},
    getCurrentOrgRole: () => null,
    hasOrgAccess: () => true,
    checkBillingStatus: fetchBillingStatus,
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};
